import { Selection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
import type { EditorInsertContentCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';

import { CommandHandler, type PassThroughCommandProps } from './command-handler';

/**
 * Handles the {@link EditorInsertContentCommand} by delegating to the host-provided
 * `onInsertContent` callback, which wires the actual insert/replace logic into the editor.
 */
export class InsertContentCommandHandler extends CommandHandler<
	EditorInsertContentCommandProperties,
	void
> {
	constructor(props: PassThroughCommandProps) {
		super(props);
	}

	execute(payload: EditorInsertContentCommandProperties): void {
		const { editorAPI, onInsertContent, onUploadAndInsertMedia } = this.props;
		if (!editorAPI) {
			return;
		}

		editorAPI.core.actions.execute(({ tr }) => {
			let start = tr.selection.from,
				end = tr.selection.to;
			if (payload.placement === 'after-block') {
				if (payload.useStoredSelection) {
					const savedSelection =
						editorAPI?.malleableUi?.sharedState?.currentState()?.savedSelection;
					if (savedSelection) {
						const { from, to, anchorCellPos, headCellPos } = savedSelection;
						start = Math.min(
							from,
							anchorCellPos ?? Number.MAX_VALUE,
							headCellPos ?? Number.MAX_VALUE,
						);
						end = Math.max(to, anchorCellPos ?? 0, headCellPos ?? 0);
					}
				}

				const sharedDepth = tr.doc.resolve(start).sharedDepth(end);
				if (sharedDepth > 0) {
					const $end = tr.doc.resolve(end);
					const pos = fg('platform_editor_remix_insert_at_top_level')
						? $end.after(1)
						: $end.after(sharedDepth);
					start = end = pos;
				}
			}

			// upload-and-insert-media is async and handled separately via the React-layer callback
			// we are only setting the selection here.
			if (payload.mode === 'upload-and-insert-media' && onUploadAndInsertMedia) {
				void onUploadAndInsertMedia(payload.data.url);
				const selection = Selection.findFrom(tr.doc.resolve(end), 1);
				return selection ? tr.setSelection(selection) : tr;
			}

			return (
				editorAPI.aiExperience?.commands.insertContent(payload, [start, end])?.({ tr }) ?? null
			);
		});

		onInsertContent?.(payload);
	}
}
